'use client';
import React, { useState, useEffect } from 'react';
import Image from 'next/image';

const navLinks = [
  { label: 'Home', href: '#' },
  { label: 'Articles', href: '#articles' },
  { label: 'Theories', href: '#theories' },
  { label: 'Videos', href: '#videos' },
  { label: 'Topics', href: '#topics' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchVal, setSearchVal] = useState('');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[#070a14] shadow-[0_2px_16px_rgba(0,0,0,0.8)] border-b border-[#1e2a3a]'
          : 'bg-[#070a14] border-b border-[#1e2a3a]'
      }`}
      style={{ fontFamily: 'Roboto, sans-serif' }}
    >
      <nav className="max-w-[1280px] mx-auto px-6 flex items-center justify-between h-[72px]">
        {/* Logo */}
        <a href="#" className="flex items-center gap-3 flex-shrink-0">
          <Image
            src="/assets/images/Untitled_design__2_-1781814632158.png"
            alt="The Inceptions Logo"
            width={48}
            height={48}
            className="rounded-sm object-cover"
          />
          <span
            className="text-white font-bold text-lg tracking-widest uppercase hidden sm:block"
            style={{ fontFamily: 'Montserrat, sans-serif', letterSpacing: '0.12em' }}
          >
            THE INCEPTIONS
          </span>
        </a>

        {/* Desktop Nav Links */}
        <ul className="hidden lg:flex items-center gap-1">
          {navLinks?.map((link) => (
            <li key={link?.label}>
              <a
                href={link?.href}
                className="px-4 py-2 text-[14px] text-[#c8d0e0] hover:text-white hover:bg-[#1e2a3a] rounded transition-all duration-200"
                style={{ fontFamily: 'Roboto, sans-serif' }}
              >
                {link?.label}
              </a>
            </li>
          ))}
        </ul>

        {/* Right Actions */}
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative hidden md:flex items-center">
            {searchOpen ? (
              <div className="flex items-center bg-[#111827] border border-[#2d3748] rounded overflow-hidden">
                <input
                  type="text"
                  value={searchVal}
                  onChange={(e) => setSearchVal(e?.target?.value)}
                  placeholder="Search articles..."
                  className="bg-transparent text-white text-sm px-3 py-1.5 outline-none w-48 placeholder-[#4a5568]"
                  autoFocus
                />
                <button
                  onClick={() => { setSearchOpen(false); setSearchVal(''); }}
                  className="px-2 py-1.5 text-[#a0aec0] hover:text-white transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <button
                onClick={() => setSearchOpen(true)}
                className="p-2 text-[#a0aec0] hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            )}
          </div>

          {/* Subscribe CTA */}
          <a
            href="#subscribe"
            className="hidden md:inline-flex items-center gap-2 bg-[#e01a2e] hover:bg-[#c0152a] text-white text-[13px] font-semibold px-4 py-2 rounded-[3px] transition-all duration-200"
            style={{ fontFamily: 'Roboto, sans-serif' }}
          >
            Subscribe Free
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>

          {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden p-2 text-[#a0aec0] hover:text-white transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </nav>
      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-[#0d1120] border-t border-[#1e2a3a] px-6 py-4">
          <ul className="flex flex-col gap-1">
            {navLinks?.map((link) => (
              <li key={link?.label}>
                <a
                  href={link?.href}
                  className="block px-3 py-2.5 text-[14px] text-[#c8d0e0] hover:text-white hover:bg-[#1e2a3a] rounded transition-all duration-200"
                  onClick={() => setMobileOpen(false)}
                >
                  {link?.label}
                </a>
              </li>
            ))}
          </ul>
          <div className="mt-4 pt-4 border-t border-[#1e2a3a]">
            <a
              href="#subscribe"
              className="block text-center bg-[#e01a2e] hover:bg-[#c0152a] text-white text-[13px] font-semibold px-4 py-2.5 rounded-[3px] transition-all duration-200"
            >
              Subscribe Free
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
